import { AxiosInstance } from 'axios';
import { DataFetcherResponse } from '@/compiler/types';
import baseAxios from '@/helpers/forms/axios';
import type { PartnerAppStatus } from '../platformOauthPartnerApps';
import type { UserUuid } from '../users';

export interface AdminPartnerApp {
    id: string
    name: string
    description: string | null
    logoUrl: string | null
    websiteUrl: string | null
    installUrl: string | null
    redirectUris: string[]
    allowedScopes: string[]
    status: PartnerAppStatus
    approvedAt: string | null
    listedAt: string | null
    suspendedAt: string | null
    revokedAt: string | null
    createdAt: string | null
    updatedAt: string | null
    ownerUserUuid: UserUuid | null
}

export interface AdminPartnerAppOwner {
    uuid: UserUuid
    name: string
    email: string
}

export interface AdminPartnerAppDetail extends AdminPartnerApp {
    owner: AdminPartnerAppOwner | null
    activeGrantCount: number
}

export type AdminPartnerAppLifecycleAction = 'approve' | 'unapprove' | 'suspend' | 'reinstate' | 'list' | 'unlist';

export interface AdminPartnerAppSearch {
    page: number
    perPage: number
    name: string
    status: PartnerAppStatus | null
}

const partnerAppsUrl = '/api/admin/partner-apps';

const partnerAppUrl = (appId: string): string => `${partnerAppsUrl}/${encodeURIComponent(appId)}`;

export default (axios: AxiosInstance = baseAxios): {
    searchPartnerApps: (search: AdminPartnerAppSearch, signal?: AbortSignal) => Promise<DataFetcherResponse<AdminPartnerApp>>
    fetchPartnerApp: (appId: string) => Promise<AdminPartnerAppDetail>
    approvePartnerApp: (appId: string) => Promise<AdminPartnerAppDetail>
    unapprovePartnerApp: (appId: string) => Promise<AdminPartnerAppDetail>
    suspendPartnerApp: (appId: string) => Promise<AdminPartnerAppDetail>
    reinstatePartnerApp: (appId: string) => Promise<AdminPartnerAppDetail>
    listPartnerApp: (appId: string) => Promise<AdminPartnerAppDetail>
    unlistPartnerApp: (appId: string) => Promise<AdminPartnerAppDetail>
} => {
    const changeLifecycle = async (appId: string, action: AdminPartnerAppLifecycleAction): Promise<AdminPartnerAppDetail> => {
        const response = await axios.post<{ data: AdminPartnerAppDetail }>(`${partnerAppUrl(appId)}/${action}`);

        return response.data.data;
    };

    return {
        searchPartnerApps: async (search: AdminPartnerAppSearch, signal?: AbortSignal): Promise<DataFetcherResponse<AdminPartnerApp>> => {
            const name = search.name.trim();
            const response = await axios.get<DataFetcherResponse<AdminPartnerApp>>(partnerAppsUrl, {
                params: {
                    page: search.page,
                    perPage: search.perPage,
                    ...(name === '' ? {} : { name }),
                    ...(search.status === null ? {} : { status: search.status }),
                },
                signal,
            });

            return response.data;
        },

        fetchPartnerApp: async (appId: string): Promise<AdminPartnerAppDetail> => {
            const response = await axios.get<{ data: AdminPartnerAppDetail }>(partnerAppUrl(appId));

            return response.data.data;
        },

        approvePartnerApp: (appId: string): Promise<AdminPartnerAppDetail> => changeLifecycle(appId, 'approve'),
        unapprovePartnerApp: (appId: string): Promise<AdminPartnerAppDetail> => changeLifecycle(appId, 'unapprove'),
        suspendPartnerApp: (appId: string): Promise<AdminPartnerAppDetail> => changeLifecycle(appId, 'suspend'),
        reinstatePartnerApp: (appId: string): Promise<AdminPartnerAppDetail> => changeLifecycle(appId, 'reinstate'),
        listPartnerApp: (appId: string): Promise<AdminPartnerAppDetail> => changeLifecycle(appId, 'list'),
        unlistPartnerApp: (appId: string): Promise<AdminPartnerAppDetail> => changeLifecycle(appId, 'unlist'),
    };
};
